// =============================================================================
// PARAMETERS BAR - Візуалізація 5 ключових параметрів (Tactical HUD)
// =============================================================================

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ParametersBarProps {
  militaryStrength: number;
  economy: number;
  resilience: number;
  westernSupport: number;
  trust: number;
  decisionNumber: number;
  maxDecisions: number;
  onLogoClick?: () => void;
  onViewRules?: () => void;
  onCreateGame?: () => void;
  onShare?: () => void;
  onFeedback?: () => void;
  isLoading?: boolean;
}

/**
 * Отримати рівень залежно від значення
 */
function getLevel(value: number): 'low' | 'medium' | 'high' {
  if (value >= 70) return 'high';
  if (value >= 40) return 'medium';
  return 'low';
}

function getLevelClass(value: number): string {
  const level = getLevel(value);
  if (level === 'high') return 'text-war-bright';
  if (level === 'medium') return 'text-war-amber';
  return 'text-war-red';
}

function getBarClass(value: number): string {
  const level = getLevel(value);
  if (level === 'high')
    return 'bg-war-primary shadow-[0_0_5px_theme(colors.war-primary)]';
  if (level === 'medium')
    return 'bg-war-amber shadow-[0_0_5px_theme(colors.war-amber)]';
  return 'bg-war-red shadow-[0_0_5px_theme(colors.war-red)]';
}

interface ParameterModuleProps {
  label: string;
  description: string;
  value: number;
  index: number;
}

function ParameterModule({
  label,
  description,
  value,
  index,
}: ParameterModuleProps) {
  return (
    <motion.div
      className="bg-war-panel/60 border border-war-dim/20 p-3 md:p-4 flex flex-col gap-2"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: 0.2 + index * 0.3,
        duration: 0.8,
        ease: 'easeOut',
      }}
    >
      <div className="flex justify-between items-start gap-2">
        <span className="text-[10px] md:text-xs font-mono text-war-primary/60 uppercase tracking-wider">
          {label}
        </span>
        <span
          className={`font-mono text-xs md:text-sm font-bold ${getLevelClass(
            value,
          )} animate-pulse-slow`}
        >
          {Math.round(value)}%
        </span>
      </div>

      <div className="text-[9px] md:text-[10px] text-war-dim font-mono uppercase leading-none truncate">
        {description}
      </div>

      <div className="h-1 md:h-1.5 bg-war-bg/80 overflow-hidden mt-1">
        <motion.div
          className={`h-full ${getBarClass(value)}`}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{
            delay: 0.4 + index * 0.3,
            duration: 1.6,
            ease: 'easeOut',
          }}
        />
      </div>
    </motion.div>
  );
}

export function ParametersBar({
  militaryStrength,
  economy,
  resilience,
  westernSupport,
  trust,
  decisionNumber,
  maxDecisions,
  onLogoClick,
  onViewRules,
  onCreateGame,
  onShare,
  onFeedback,
}: Readonly<ParametersBarProps>) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSurrenderConfirm, setShowSurrenderConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleSurrenderClick = () => {
    setIsMenuOpen(false);
    setShowSurrenderConfirm(true);
  };

  const handleSurrenderConfirm = () => {
    setShowSurrenderConfirm(false);
    if (onLogoClick) {
      onLogoClick();
    }
  };

  const handleSurrenderCancel = () => {
    setShowSurrenderConfirm(false);
  };

  const menuItems = [
    { label: 'Статут', action: onViewRules },
    { label: 'Додати збір/петицію', action: onCreateGame },
    { label: 'Мобілізувати друзів', action: onShare },
    { label: "Зв'язок зі штабом", action: onFeedback },
    { label: 'Капітулювати', action: handleSurrenderClick, danger: true },
  ];

  return (
    <section
      className="space-y-4 md:space-y-6"
      aria-label="Поточні параметри держави"
      role="region"
    >
      {/* Screen reader announcements for parameter changes */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        Військова сила: {Math.round(militaryStrength)}%, Економіка:{' '}
        {Math.round(economy)}%, Стійкість нації: {Math.round(resilience)}%,
        Підтримка Заходу: {Math.round(westernSupport)}%, Довіра до влади:{' '}
        {Math.round(trust)}%
      </div>

      {/* HUD Header */}
      <div className="flex justify-between items-center gap-4 bg-war-panel/40 border-b border-war-dim/20 px-4 py-3">
        {/* Left side: Logo + Title */}
        <div className="flex items-center gap-3">
          {/* Logo with dropdown menu (works on all devices) */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="flex items-center gap-1 opacity-60 hover:opacity-90 transition-opacity cursor-pointer"
              aria-label="Відкрити меню"
              aria-expanded={isMenuOpen}
            >
              <img
                src="/logo.png"
                alt="Logo"
                className="w-7 h-7 md:w-8 md:h-8 object-contain filter drop-shadow-[0_0_3px_rgba(16,185,129,0.1)] mix-blend-screen"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              {/* Menu indicator - small chevron */}
              <svg
                className={`w-2.5 h-2.5 text-war-primary/50 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-2 w-56 bg-war-bg/95 backdrop-blur-sm border border-war-dim/30 shadow-xl z-50"
                >
                  <div className="py-1">
                    {menuItems.map((item, index) => {
                      const action = item.action;
                      return action ? (
                        <button
                          key={index}
                          onClick={() => {
                            setIsMenuOpen(false);
                            action();
                          }}
                          className={`w-full px-4 py-2.5 text-left text-xs font-mono uppercase tracking-wider transition-colors ${item.danger
                              ? 'text-war-dim/40 hover:text-war-dim hover:bg-war-dim/10'
                              : 'text-war-dim hover:text-war-primary hover:bg-war-primary/10'
                            }`}
                        >
                          <span className="text-war-primary/50 mr-2">
                            [{String(index + 1).padStart(2, '0')}]
                          </span>
                          {item.label}
                        </button>
                      ) : null;
                    })}

                    {/* Separator */}
                    <div className="border-t border-war-dim/20 my-1" />

                    {/* Close button */}
                    <button
                      onClick={() => setIsMenuOpen(false)}
                      className="w-full px-4 py-2.5 text-left text-xs font-mono uppercase tracking-wider text-war-dim/60 hover:text-war-dim hover:bg-war-dim/10 transition-colors"
                    >
                      ← Назад
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <h2 className="text-war-primary text-sm md:text-base font-mono tracking-widest font-bold">
              СИСТЕМА УПРАВЛІННЯ
            </h2>
            <p className="text-war-dim text-[8px] md:text-[10px] uppercase">
              СТАТУС: АКТИВНИЙ
            </p>
          </div>
        </div>

        {/* Right side: Turn indicator */}
        <div className="flex items-center gap-4 md:gap-6 font-mono">
          {/* Turn indicator */}
          <div className="text-right">
            <span className="text-war-dim text-[10px] uppercase block">
              РІШЕННЯ
            </span>
            <span className="text-war-bright text-sm md:text-lg">
              {String(decisionNumber).padStart(2, '0')}{' '}
              <span className="text-war-dim">/</span> {maxDecisions}
            </span>
          </div>
        </div>
      </div>

      {/* Modules Grid - horizontal scroll on mobile with indicator */}
      <div className="relative">
        {/* Scroll indicator for mobile */}
        <div className="md:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-war-bg via-war-bg/80 to-transparent pointer-events-none z-10 flex items-center justify-end pr-1">
          <span className="text-war-dim/40 text-xs animate-pulse">→</span>
        </div>

        <div className="overflow-x-auto scrollbar-hide md:overflow-visible pb-1">
          <div className="grid grid-cols-5 gap-0.5 md:gap-1 bg-war-dim/20 shadow-2xl min-w-[600px] md:min-w-0">
            <ParameterModule
              label="ВІЙСЬКОВА"
              description="Боєздатність ЗС"
              value={militaryStrength}
              index={0}
            />
            <ParameterModule
              label="ЕКОНОМІКА"
              description="Стабільність"
              value={economy}
              index={1}
            />
            <ParameterModule
              label="СТІЙКІСТЬ"
              description="Дух нації"
              value={resilience}
              index={2}
            />
            <ParameterModule
              label="ЗАХІД"
              description="Підтримка"
              value={westernSupport}
              index={3}
            />
            <ParameterModule
              label="ДОВІРА"
              description="До влади"
              value={trust}
              index={4}
            />
          </div>
        </div>
      </div>

      {/* Surrender Confirmation Modal */}
      <AnimatePresence>
        {showSurrenderConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={handleSurrenderCancel}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-war-bg border-2 border-war-red/50 p-8 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Warning Icon */}
              <div className="text-6xl mb-6">🏳️</div>

              {/* Main Message */}
              <h2 className="text-2xl md:text-3xl font-bold text-war-red font-mono tracking-wider uppercase mb-4">
                Точно?
              </h2>
              <p className="text-war-amber text-lg md:text-xl font-mono mb-8">
                Історія це запам’ятає.
              </p>

              {/* Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleSurrenderCancel}
                  className="w-full py-3 px-6 bg-war-primary/20 border border-war-primary text-war-bright font-mono uppercase tracking-widest text-sm hover:bg-war-primary/30 transition-colors"
                >
                  Передумати
                </button>
                <button
                  onClick={handleSurrenderConfirm}
                  className="w-full py-3 px-6 bg-war-red/10 border border-war-red/50 text-war-red font-mono uppercase tracking-widest text-sm hover:bg-war-red/20 transition-colors"
                >
                  Немає совісті
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
